import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, TrendingUp, Users, DollarSign, Activity, Settings,
  AlertCircle, Send, Download, Shield, Clock, Zap, BarChart3,
  Power, PowerOff, Calendar, CreditCard, Mail, Eye, RefreshCw, LayoutDashboard, LogOut, Plus, CheckCircle,
  Trash2, Archive, History
} from 'lucide-react';
import { ToastContainer } from '../common/Toast';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

// Import the original dashboard
import SuperAdminDashboard from './SuperAdminDashboard';

interface Gym {
  gymId: string; // GUID as string
  gymName: string;
  ownerName: string;
  email: string;
  isActive: boolean;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
  isTrial: boolean;
  subscriptionPlan: string;
  subscriptionEndDate: string;
  currentMemberCount: number;
  currentStaffCount: number;
  maxMembers: number;
  maxStaff: number;
  monthlyPrice: number;
  totalRevenue: number;
  suspensionReason?: string;
  suspendedAt?: string;
  logoUrl?: string;
  updatedAt?: string;
}

interface RevenueData {
  totalRevenue: number;
  mrr: number;
  arr: number;
  activeGyms: number;
  revenueByMonth: { month: string; revenue: number; count: number }[];
  topGyms: { gymId: string; gymName: string; revenue: number; memberCount: number }[];
}

interface GrowthMetrics {
  newGymsLast30: number;
  newGymsPrevious30: number;
  growthRate: number;
  churnRate: number;
  totalMembers: number;
  avgMembersPerGym: number;
  conversionRate: number;
}

interface SystemHealth {
  status: string;
  timestamp: string;
  metrics: {
    totalGyms: number;
    activeGyms: number;
    totalUsers: number;
    activeUsersLast7Days: number;
    totalMemberships: number;
    activeMemberships: number;
    expiringSubscriptions: number;
  };
  alerts: string[];
}

const EnhancedSuperAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'subscriptions' | 'users' | 'analytics' | 'system' | 'bulk' | 'history'>('overview');
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [gymHistory, setGymHistory] = useState<Gym[]>([]);
  const [selectedGym, setSelectedGym] = useState<Gym | null>(null);
  const [loading, setLoading] = useState(false);
  const [revenueData, setRevenueData] = useState<RevenueData | null>(null);
  const [growthMetrics, setGrowthMetrics] = useState<GrowthMetrics | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const { toasts, removeToast, success, error } = useToast();

  // Modals
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [showCreateGymModal, setShowCreateGymModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  // Form states
  const [suspendReason, setSuspendReason] = useState('');
  const [extendDays, setExtendDays] = useState(30);
  const [extendReason, setExtendReason] = useState('');
  const [newPlan, setNewPlan] = useState({
    plan: 'Basic',
    maxMembers: 100,
    maxStaff: 5,
    monthlyPrice: 49
  });
  const [bulkEmail, setBulkEmail] = useState({
    subject: '',
    body: '',
    onlyActive: true,
    onlyTrial: false
  });
  const [creatingGym, setCreatingGym] = useState(false);
  const [newGymCredentials, setNewGymCredentials] = useState<any>(null);
  const [newGym, setNewGym] = useState({
    gymName: '',
    ownerName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    subscriptionPlan: 'Trial',
    logoUrl: ''
  });
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [logoFile, setLogoFile] = useState<File | null>(null);

  useEffect(() => {
    loadGyms();
    if (activeTab === 'analytics') {
      loadAnalytics();
    }
    if (activeTab === 'system') {
      loadSystemHealth();
    }
    if (activeTab === 'history') {
      loadGymHistory();
    }
  }, [activeTab]);

  const loadGyms = async () => {
    setLoading(true);
    try {
      const response = await api.get('/Gym');
      setGyms(response.data);
    } catch (err) {
      error('Failed to load gyms');
    } finally {
      setLoading(false);
    }
  };

  const loadGymHistory = async () => {
    setLoading(true);
    try {
      const response = await api.get('/Gym/History');
      setGymHistory(response.data || []);
    } catch (err: any) {
      console.error('Error loading gym history:', err);
      error(err.response?.data?.message || 'Failed to load gym history');
      setGymHistory([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const [revenue, growth] = await Promise.all([
        api.get('/SuperAdmin/Analytics/Revenue'),
        api.get('/SuperAdmin/Analytics/Growth')
      ]);
      setRevenueData(revenue.data);
      setGrowthMetrics(growth.data);
    } catch (err) {
      error('Failed to load analytics');
    }
  };

  const loadSystemHealth = async () => {
    try {
      const response = await api.get('/SuperAdmin/System/Health');
      setSystemHealth(response.data);
    } catch (err) {
      error('Failed to load system health');
    }
  };

  const handleSuspendGym = async () => {
    if (!selectedGym || !suspendReason) return;

    try {
      await api.post(`/SuperAdmin/Gym/${selectedGym.gymId}/Suspend`, { reason: suspendReason });
      success('Gym suspended successfully');
      setShowSuspendModal(false);
      setSuspendReason('');
      setSelectedGym(null);
      loadGyms();
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to suspend gym');
    }
  };

  const handleReactivateGym = async (gymId: string) => {
    try {
      await api.post(`/SuperAdmin/Gym/${gymId}/Reactivate`);
      success('Gym reactivated successfully');
      loadGyms();
      if (activeTab === 'history') {
        loadGymHistory();
      }
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to reactivate gym');
    }
  };

  const handleDeactivateGym = async () => {
    if (!selectedGym) return;

    try {
      await api.put(`/Gym/${selectedGym.gymId}/Deactivate`);
      success('Gym deactivated successfully');
      setShowDeactivateModal(false);
      setSelectedGym(null);
      loadGyms();
      if (activeTab === 'history') {
        loadGymHistory();
      }
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to deactivate gym');
    }
  };

  const handleDeleteGym = async () => {
    if (!selectedGym) return;

    try {
      await api.delete(`/Gym/${selectedGym.gymId}`);
      success('Gym deleted successfully');
      setShowDeleteModal(false);
      setSelectedGym(null);
      loadGyms();
      if (activeTab === 'history') {
        loadGymHistory();
      }
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to delete gym');
    }
  };

  const handleActivateGym = async (gymId: string) => {
    try {
      await api.put(`/Gym/${gymId}/Activate`);
      success('Gym activated successfully');
      loadGyms();
      if (activeTab === 'history') {
        loadGymHistory();
      }
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to activate gym');
    }
  };

  const handleDownloadGymData = async (gymId: string, gymName: string) => {
    try {
      const response = await api.get(`/Gym/${gymId}/ExportData`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${gymName.replace(/\s+/g, '_')}_Complete_Data_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      success('Gym data downloaded successfully');
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to download gym data');
    }
  };

  const handleExtendSubscription = async () => {
    if (!selectedGym) return;

    try {
      await api.post(`/SuperAdmin/Gym/${selectedGym.gymId}/ExtendSubscription`, {
        days: extendDays,
        reason: extendReason
      });
      success(`Subscription extended by ${extendDays} days`);
      setShowExtendModal(false);
      setExtendDays(30);
      setExtendReason('');
      setSelectedGym(null);
      loadGyms();
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to extend subscription');
    }
  };

  const handleChangePlan = async () => {
    if (!selectedGym) return;

    try {
      await api.post(`/SuperAdmin/Gym/${selectedGym.gymId}/ChangePlan`, {
        newPlan: newPlan.plan,
        maxMembers: newPlan.maxMembers,
        maxStaff: newPlan.maxStaff,
        monthlyPrice: newPlan.monthlyPrice
      });
      success('Plan changed successfully');
      setShowChangePlanModal(false);
      setSelectedGym(null);
      loadGyms();
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to change plan');
    }
  };

  const handleBulkEmail = async () => {
    try {
      const response = await api.post('/SuperAdmin/Bulk/Email', bulkEmail);
      success(response.data.message);
      setShowBulkEmailModal(false);
      setBulkEmail({ subject: '', body: '', onlyActive: true, onlyTrial: false });
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to send bulk email');
    }
  };

  const handleExportGyms = async () => {
    try {
      const response = await api.get('/SuperAdmin/Export/Gyms', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gyms_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      success('Gyms exported successfully');
    } catch (err) {
      error('Failed to export gyms');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        error('Image size should be less than 5MB');
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        error('Please select an image file');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setLogoPreview(base64String);
        setLogoFile(file);
        setNewGym({ ...newGym, logoUrl: base64String });
      };
      reader.readAsDataURL(file);
    }
    
    // Reset file input
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleCreateGym = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingGym(true);
    try {
      const response = await api.post('/Gym', newGym);
      success('Gym created successfully!');
      setShowCreateGymModal(false);
      
      // Show credentials modal with login details
      if (response.data.loginCredentials) {
        setNewGymCredentials({
          gymName: newGym.gymName,
          email: response.data.loginCredentials.email,
          password: response.data.loginCredentials.temporaryPassword,
          message: response.data.loginCredentials.message
        });
        setShowCredentialsModal(true);
      }
      
      setNewGym({
        gymName: '',
        ownerName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        country: '',
        subscriptionPlan: 'Trial',
        logoUrl: ''
      });
      setLogoPreview('');
      setLogoFile(null);
      
      // Reload gyms list
      loadGyms();
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to create gym');
      console.error(err);
    } finally {
      setCreatingGym(false);
    }
  };

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: BarChart3 },
    { id: 'subscriptions' as const, label: 'Subscriptions', icon: CreditCard },
    { id: 'users' as const, label: 'Users', icon: Users },
    { id: 'analytics' as const, label: 'Analytics', icon: TrendingUp },
    { id: 'system' as const, label: 'System', icon: Activity },
    { id: 'bulk' as const, label: 'Bulk Operations', icon: Send },
    { id: 'history' as const, label: 'Gym History', icon: History }
  ];

  return (
    <div style={{ backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
      {/* Sticky Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        backgroundColor: 'white',
        borderBottom: '1px solid #E5E7EB',
        padding: '1.5rem 2rem',
        zIndex: 100,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
              <Shield size={32} style={{ color: '#6366F1' }} />
              <h1 style={{ fontSize: '1.75rem', fontWeight: 'bold', margin: 0 }}>
                Super Admin Control Center
              </h1>
            </div>
            <p style={{ color: '#6B7280', fontSize: '0.875rem', margin: 0 }}>
              Welcome back, <strong>{user?.firstName} {user?.lastName}</strong> • Advanced platform management
            </p>
          </div>
        
          {/* Navigation Buttons */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.625rem 1rem',
                backgroundColor: '#10B981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.875rem',
                transition: 'all 0.2s',
                boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#059669';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#10B981';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.2)';
              }}
            >
              <LayoutDashboard size={16} />
              <span style={{ display: 'none' }} className="button-text">Gym Dashboard</span>
              <span className="button-text-mobile">Dashboard</span>
            </button>

            <button
              onClick={() => setShowCreateGymModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.625rem 1rem',
                backgroundColor: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.875rem',
                transition: 'all 0.2s',
                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2563EB';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3B82F6';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.2)';
              }}
            >
              <Plus size={16} />
              <span>Create Gym</span>
            </button>
            
            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.625rem 1rem',
                backgroundColor: '#EF4444',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.875rem',
                transition: 'all 0.2s',
                boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#DC2626';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#EF4444';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.2)';
              }}
            >
              <LogOut size={16} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Main Content Area */}
      <div style={{ padding: '2rem' }}>

        {/* Tabs */}
        <div style={{ borderBottom: '2px solid #E5E7EB', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', overflowX: 'auto' }}>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: '0.75rem 1.25rem',
                    backgroundColor: isActive ? '#EEF2FF' : 'transparent',
                    border: 'none',
                    borderBottom: isActive ? '3px solid #6366F1' : '3px solid transparent',
                    color: isActive ? '#6366F1' : '#6B7280',
                    fontWeight: isActive ? '600' : '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s',
                    borderRadius: '8px 8px 0 0',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = '#F9FAFB';
                      e.currentTarget.style.color = '#374151';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#6B7280';
                    }
                  }}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <SuperAdminDashboard />
      )}

      {activeTab === 'subscriptions' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Subscription Management</h2>
            <button
              onClick={handleExportGyms}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#6366F1',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Download size={18} />
              Export Gyms
            </button>
          </div>

          {/* Gym Cards with Actions */}
          <div style={{ display: 'grid', gap: '1rem' }}>
            {gyms.map((gym) => (
              <div
                key={gym.gymId}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  border: '1px solid #E5E7EB',
                  opacity: gym.isActive ? 1 : 0.6
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{gym.gymName}</h3>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: gym.isActive ? '#DEF7EC' : '#FEE2E2',
                        color: gym.isActive ? '#03543F' : '#991B1B'
                      }}>
                        {gym.isActive ? 'Active' : 'Suspended'}
                      </span>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: gym.isTrial ? '#FEF3C7' : '#DBEAFE',
                        color: gym.isTrial ? '#92400E' : '#1E40AF'
                      }}>
                        {gym.subscriptionPlan}
                      </span>
                    </div>
                    
                    <div style={{ color: '#6B7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
                      <p>Owner: {gym.ownerName} • {gym.email}</p>
                      <p>Members: {gym.currentMemberCount}/{gym.maxMembers} • Staff: {gym.currentStaffCount}/{gym.maxStaff}</p>
                      <p>MRR: ₹{gym.monthlyPrice} • Total Revenue: ₹{gym.totalRevenue.toFixed(2)}</p>
                      <p>Expires: {new Date(gym.subscriptionEndDate).toLocaleDateString()}</p>
                      {gym.suspensionReason && (
                        <p style={{ color: '#DC2626', marginTop: '0.5rem' }}>
                          <AlertCircle size={14} style={{ display: 'inline', marginRight: '0.25rem' }} />
                          Reason: {gym.suspensionReason}
                        </p>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {gym.isActive ? (
                      <button
                        onClick={() => {
                          setSelectedGym(gym);
                          setShowSuspendModal(true);
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#FEE2E2',
                          color: '#991B1B',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          fontSize: '0.875rem'
                        }}
                      >
                        <PowerOff size={16} />
                        Suspend
                      </button>
                    ) : (
                      <button
                        onClick={() => handleReactivateGym(gym.gymId)}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#DEF7EC',
                          color: '#03543F',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          fontSize: '0.875rem'
                        }}
                      >
                        <Power size={16} />
                        Reactivate
                      </button>
                    )}

                    <button
                      onClick={() => {
                        setSelectedGym(gym);
                        setShowExtendModal(true);
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#DBEAFE',
                        color: '#1E40AF',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.875rem'
                      }}
                    >
                      <Calendar size={16} />
                      Extend
                    </button>

                    <button
                      onClick={() => {
                        setSelectedGym(gym);
                        setShowChangePlanModal(true);
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#E0E7FF',
                        color: '#3730A3',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.875rem'
                      }}
                    >
                      <RefreshCw size={16} />
                      Change Plan
                    </button>

                    {gym.isActive && (
                      <>
                        <button
                          onClick={() => {
                            setSelectedGym(gym);
                            setShowDeactivateModal(true);
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#FEF3C7',
                            color: '#92400E',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '0.875rem'
                          }}
                        >
                          <Archive size={16} />
                          Deactivate
                        </button>
                        <button
                          onClick={() => {
                            setSelectedGym(gym);
                            setShowDeleteModal(true);
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#FEE2E2',
                            color: '#991B1B',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '0.875rem'
                          }}
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleDownloadGymData(gym.gymId, gym.gymName)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#D1FAE5',
                        color: '#065F46',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.875rem'
                      }}
                    >
                      <Download size={16} />
                      Download Data
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Gym History</h2>
            <p style={{ color: '#6B7280', fontSize: '0.875rem' }}>
              Deleted and deactivated gyms
            </p>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Loading gym history...</div>
          ) : gymHistory.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
              <History size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
              <p>No gym history found</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {gymHistory.map((gym) => (
                <div
                  key={gym.gymId}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    border: '1px solid #E5E7EB',
                    opacity: 0.8
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{gym.gymName}</h3>
                        {gym.isDeleted ? (
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            backgroundColor: '#FEE2E2',
                            color: '#991B1B'
                          }}>
                            Deleted
                          </span>
                        ) : (
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            backgroundColor: '#FEF3C7',
                            color: '#92400E'
                          }}>
                            Deactivated
                          </span>
                        )}
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          backgroundColor: gym.isTrial ? '#FEF3C7' : '#DBEAFE',
                          color: gym.isTrial ? '#92400E' : '#1E40AF'
                        }}>
                          {gym.subscriptionPlan}
                        </span>
                      </div>
                      
                      <div style={{ color: '#6B7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
                        <p>Owner: {gym.ownerName} • {gym.email}</p>
                        <p>Members: {gym.currentMemberCount}/{gym.maxMembers} • Staff: {gym.currentStaffCount}/{gym.maxStaff}</p>
                        <p>MRR: ₹{gym.monthlyPrice} • Total Revenue: ₹{gym.totalRevenue.toFixed(2)}</p>
                        {gym.isDeleted && gym.deletedAt && (
                          <p style={{ color: '#DC2626', marginTop: '0.5rem' }}>
                            Deleted on: {new Date(gym.deletedAt).toLocaleDateString()} by {gym.deletedBy || 'SuperAdmin'}
                          </p>
                        )}
                        {!gym.isDeleted && gym.updatedAt && (
                          <p style={{ color: '#F59E0B', marginTop: '0.5rem' }}>
                            Deactivated on: {new Date(gym.updatedAt).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {!gym.isDeleted && !gym.isActive && (
                        <button
                          onClick={() => handleActivateGym(gym.gymId)}
                          style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#DEF7EC',
                            color: '#03543F',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '0.875rem'
                          }}
                        >
                          <Power size={16} />
                          Activate
                        </button>
                      )}
                      <button
                        onClick={() => handleDownloadGymData(gym.gymId, gym.gymName)}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#D1FAE5',
                          color: '#065F46',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          fontSize: '0.875rem'
                        }}
                      >
                        <Download size={16} />
                        Download Data
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'analytics' && (
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>Financial Analytics</h2>
          
          {revenueData && growthMetrics && (
            <>
              {/* Revenue Overview Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', border: '1px solid #E5E7EB' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#6B7280', fontSize: '0.875rem' }}>Total Revenue</span>
                    <DollarSign size={20} style={{ color: '#10B981' }} />
                  </div>
                  <p style={{ fontSize: '2rem', fontWeight: 'bold' }}>₹{revenueData.totalRevenue.toLocaleString()}</p>
                </div>

                <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', border: '1px solid #E5E7EB' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#6B7280', fontSize: '0.875rem' }}>MRR</span>
                    <TrendingUp size={20} style={{ color: '#3B82F6' }} />
                  </div>
                  <p style={{ fontSize: '2rem', fontWeight: 'bold' }}>₹{revenueData.mrr.toLocaleString()}</p>
                  <p style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.5rem' }}>
                    ARR: ₹{revenueData.arr.toLocaleString()}
                  </p>
                </div>

                <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', border: '1px solid #E5E7EB' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#6B7280', fontSize: '0.875rem' }}>Growth Rate</span>
                    <TrendingUp size={20} style={{ color: '#10B981' }} />
                  </div>
                  <p style={{ fontSize: '2rem', fontWeight: 'bold' }}>{growthMetrics.growthRate.toFixed(1)}%</p>
                  <p style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.5rem' }}>
                    +{growthMetrics.newGymsLast30} gyms last 30 days
                  </p>
                </div>

                <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', border: '1px solid #E5E7EB' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#6B7280', fontSize: '0.875rem' }}>Churn Rate</span>
                    <AlertCircle size={20} style={{ color: '#EF4444' }} />
                  </div>
                  <p style={{ fontSize: '2rem', fontWeight: 'bold' }}>{growthMetrics.churnRate.toFixed(1)}%</p>
                </div>
              </div>

              {/* Top Performing Gyms */}
              <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', border: '1px solid #E5E7EB', marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>Top Performing Gyms</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600' }}>Gym Name</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600' }}>Revenue</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600' }}>Members</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueData.topGyms.map((gym, idx) => (
                        <tr key={gym.gymId} style={{ borderBottom: '1px solid #F3F4F6' }}>
                          <td style={{ padding: '0.75rem' }}>{idx + 1}. {gym.gymName}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>₹{gym.revenue.toLocaleString()}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{gym.memberCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Revenue by Month Chart */}
              <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', border: '1px solid #E5E7EB' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>Monthly Revenue Trend</h3>
                <div style={{ height: '300px', display: 'flex', alignItems: 'flex-end', gap: '1rem', padding: '1rem 0' }}>
                  {revenueData.revenueByMonth.map((month) => {
                    const maxRevenue = Math.max(...revenueData.revenueByMonth.map(m => m.revenue));
                    const height = (month.revenue / maxRevenue) * 100;
                    
                    return (
                      <div
                        key={month.month}
                        style={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        <div style={{
                          width: '100%',
                          height: `${height}%`,
                          backgroundColor: '#6366F1',
                          borderRadius: '4px 4px 0 0',
                          position: 'relative',
                          minHeight: '20px'
                        }}>
                          <div style={{
                            position: 'absolute',
                            top: '-1.5rem',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            whiteSpace: 'nowrap'
                          }}>
                            ₹{(month.revenue / 1000).toFixed(1)}k
                          </div>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6B7280', whiteSpace: 'nowrap' }}>
                          {month.month}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'system' && systemHealth && (
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>System Health & Monitoring</h2>
          
          {/* Status Badge */}
          <div style={{
            backgroundColor: systemHealth.status === 'Healthy' ? '#DEF7EC' : '#FEE2E2',
            border: `2px solid ${systemHealth.status === 'Healthy' ? '#10B981' : '#EF4444'}`,
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <Zap size={32} style={{ color: systemHealth.status === 'Healthy' ? '#10B981' : '#EF4444' }} />
            <div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                System Status: {systemHealth.status}
              </h3>
              <p style={{ color: '#6B7280', fontSize: '0.875rem' }}>
                Last checked: {new Date(systemHealth.timestamp).toLocaleString()}
              </p>
            </div>
          </div>

          {/* Alerts */}
          {systemHealth.alerts.length > 0 && (
            <div style={{ backgroundColor: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: '12px', padding: '1rem', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <AlertCircle size={20} style={{ color: '#D97706' }} />
                <h4 style={{ fontWeight: '600' }}>Active Alerts</h4>
              </div>
              <ul style={{ marginLeft: '1.5rem', color: '#92400E' }}>
                {systemHealth.alerts.map((alert, idx) => (
                  <li key={idx}>{alert}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Metrics Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', border: '1px solid #E5E7EB' }}>
              <div style={{ color: '#6B7280', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Total Gyms</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{systemHealth.metrics.totalGyms}</div>
              <div style={{ color: '#10B981', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                {systemHealth.metrics.activeGyms} active
              </div>
            </div>

            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', border: '1px solid #E5E7EB' }}>
              <div style={{ color: '#6B7280', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Total Users</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{systemHealth.metrics.totalUsers}</div>
              <div style={{ color: '#3B82F6', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                {systemHealth.metrics.activeUsersLast7Days} active (7 days)
              </div>
            </div>

            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', border: '1px solid #E5E7EB' }}>
              <div style={{ color: '#6B7280', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Memberships</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{systemHealth.metrics.totalMemberships}</div>
              <div style={{ color: '#10B981', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                {systemHealth.metrics.activeMemberships} active
              </div>
            </div>

            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', border: '1px solid #E5E7EB' }}>
              <div style={{ color: '#6B7280', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Expiring Soon</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: systemHealth.metrics.expiringSubscriptions > 0 ? '#EF4444' : '#10B981' }}>
                {systemHealth.metrics.expiringSubscriptions}
              </div>
              <div style={{ color: '#6B7280', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                subscriptions
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'bulk' && (
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>Bulk Operations</h2>
          
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            {/* Bulk Email */}
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', border: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <Mail size={24} style={{ color: '#6366F1' }} />
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Send Bulk Email</h3>
              </div>
              <p style={{ color: '#6B7280', marginBottom: '1rem' }}>
                Send announcements, updates, or notifications to multiple gyms at once.
              </p>
              <button
                onClick={() => setShowBulkEmailModal(true)}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6366F1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Send size={18} />
                Compose Email
              </button>
            </div>

            {/* Export Data */}
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', border: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <Download size={24} style={{ color: '#10B981' }} />
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Export Data</h3>
              </div>
              <p style={{ color: '#6B7280', marginBottom: '1rem' }}>
                Export gym data, users, memberships, and more to CSV format.
              </p>
              <button
                onClick={handleExportGyms}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Download size={18} />
                Export All Gyms
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>User Management</h2>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            border: '1px solid #E5E7EB',
            textAlign: 'center'
          }}>
            <Users size={48} style={{ color: '#6B7280', margin: '0 auto 1rem' }} />
            <p style={{ color: '#6B7280' }}>User management features coming soon...</p>
            <p style={{ color: '#9CA3AF', fontSize: '0.875rem', marginTop: '0.5rem' }}>
              View all users, reset passwords, manage roles, and more.
            </p>
          </div>
        </div>
      )}

      {/* Suspend Gym Modal */}
      {showSuspendModal && selectedGym && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              Suspend Gym: {selectedGym.gymName}
            </h3>
            <p style={{ color: '#6B7280', marginBottom: '1.5rem' }}>
              This will suspend all access for this gym. The gym owner will be notified via email.
            </p>
            
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Reason for Suspension *
            </label>
            <textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Enter reason for suspension..."
              rows={4}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '1rem',
                marginBottom: '1.5rem'
              }}
            />

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowSuspendModal(false);
                  setSuspendReason('');
                  setSelectedGym(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSuspendGym}
                disabled={!suspendReason}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#EF4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: suspendReason ? 'pointer' : 'not-allowed',
                  opacity: suspendReason ? 1 : 0.5
                }}
              >
                Suspend Gym
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extend Subscription Modal */}
      {showExtendModal && selectedGym && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              Extend Subscription: {selectedGym.gymName}
            </h3>
            
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Extend by (days) *
            </label>
            <input
              type="number"
              value={extendDays}
              onChange={(e) => setExtendDays(parseInt(e.target.value))}
              min="1"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '1rem',
                marginBottom: '1rem'
              }}
            />

            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Reason (optional)
            </label>
            <textarea
              value={extendReason}
              onChange={(e) => setExtendReason(e.target.value)}
              placeholder="Enter reason for extension..."
              rows={3}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '1rem',
                marginBottom: '1.5rem'
              }}
            />

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowExtendModal(false);
                  setExtendDays(30);
                  setExtendReason('');
                  setSelectedGym(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleExtendSubscription}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Extend Subscription
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Plan Modal */}
      {showChangePlanModal && selectedGym && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              Change Plan: {selectedGym.gymName}
            </h3>
            
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              New Plan *
            </label>
            <select
              value={newPlan.plan}
              onChange={(e) => {
                const plan = e.target.value;
                const plans: any = {
                  'Trial': { maxMembers: 50, maxStaff: 3, monthlyPrice: 0 },
                  'Basic': { maxMembers: 100, maxStaff: 5, monthlyPrice: 49 },
                  'Pro': { maxMembers: 300, maxStaff: 15, monthlyPrice: 99 },
                  'Enterprise': { maxMembers: 999, maxStaff: 50, monthlyPrice: 199 }
                };
                setNewPlan({ plan, ...plans[plan] });
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '1rem',
                marginBottom: '1rem'
              }}
            >
              <option value="Trial">Trial (Free)</option>
              <option value="Basic">Basic (₹49/month)</option>
              <option value="Pro">Pro (₹99/month)</option>
              <option value="Enterprise">Enterprise (₹199/month)</option>
            </select>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem' }}>
                  Max Members
                </label>
                <input
                  type="number"
                  value={newPlan.maxMembers}
                  onChange={(e) => setNewPlan({ ...newPlan, maxMembers: parseInt(e.target.value) })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem' }}>
                  Max Staff
                </label>
                <input
                  type="number"
                  value={newPlan.maxStaff}
                  onChange={(e) => setNewPlan({ ...newPlan, maxStaff: parseInt(e.target.value) })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>

            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Monthly Price (₹)
            </label>
            <input
              type="number"
              value={newPlan.monthlyPrice}
              onChange={(e) => setNewPlan({ ...newPlan, monthlyPrice: parseFloat(e.target.value) })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '1rem',
                marginBottom: '1.5rem'
              }}
            />

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowChangePlanModal(false);
                  setSelectedGym(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleChangePlan}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6366F1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Change Plan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Email Modal */}
      {showBulkEmailModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              Send Bulk Email
            </h3>
            
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Subject *
            </label>
            <input
              type="text"
              value={bulkEmail.subject}
              onChange={(e) => setBulkEmail({ ...bulkEmail, subject: e.target.value })}
              placeholder="Email subject..."
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '1rem',
                marginBottom: '1rem'
              }}
            />

            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Message *
            </label>
            <textarea
              value={bulkEmail.body}
              onChange={(e) => setBulkEmail({ ...bulkEmail, body: e.target.value })}
              placeholder="Email message (HTML supported)..."
              rows={8}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '1rem',
                marginBottom: '1rem',
                fontFamily: 'monospace'
              }}
            />

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={bulkEmail.onlyActive}
                  onChange={(e) => setBulkEmail({ ...bulkEmail, onlyActive: e.target.checked, onlyTrial: false })}
                  style={{ width: '18px', height: '18px' }}
                />
                <span>Only send to active gyms</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={bulkEmail.onlyTrial}
                  onChange={(e) => setBulkEmail({ ...bulkEmail, onlyTrial: e.target.checked, onlyActive: false })}
                  style={{ width: '18px', height: '18px' }}
                />
                <span>Only send to trial gyms</span>
              </label>
            </div>

            <div style={{ backgroundColor: '#F3F4F6', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
              <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                This email will be sent to approximately{' '}
                <strong>
                  {bulkEmail.onlyActive 
                    ? gyms.filter(g => g.isActive).length 
                    : bulkEmail.onlyTrial 
                    ? gyms.filter(g => g.isTrial).length 
                    : gyms.length}
                </strong> gyms.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowBulkEmailModal(false);
                  setBulkEmail({ subject: '', body: '', onlyActive: true, onlyTrial: false });
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkEmail}
                disabled={!bulkEmail.subject || !bulkEmail.body}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6366F1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: bulkEmail.subject && bulkEmail.body ? 'pointer' : 'not-allowed',
                  opacity: bulkEmail.subject && bulkEmail.body ? 1 : 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Send size={18} />
                Send Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Gym Modal */}
      {showCreateGymModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
              Create New Gym
            </h2>

            <form onSubmit={handleCreateGym}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Gym Name *</label>
                  <input
                    type="text"
                    required
                    value={newGym.gymName}
                    onChange={(e) => setNewGym({ ...newGym, gymName: e.target.value })}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '1rem' }}
                    placeholder="Enter gym name"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Owner Name *</label>
                  <input
                    type="text"
                    required
                    value={newGym.ownerName}
                    onChange={(e) => setNewGym({ ...newGym, ownerName: e.target.value })}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '1rem' }}
                    placeholder="Enter owner name"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Email *</label>
                  <input
                    type="email"
                    required
                    value={newGym.email}
                    onChange={(e) => setNewGym({ ...newGym, email: e.target.value })}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '1rem' }}
                    placeholder="Enter email"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Phone *</label>
                  <input
                    type="tel"
                    required
                    value={newGym.phone}
                    onChange={(e) => setNewGym({ ...newGym, phone: e.target.value })}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '1rem' }}
                    placeholder="Enter phone number"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Address *</label>
                  <input
                    type="text"
                    required
                    value={newGym.address}
                    onChange={(e) => setNewGym({ ...newGym, address: e.target.value })}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '1rem' }}
                    placeholder="Enter address"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>City *</label>
                    <input
                      type="text"
                      required
                      value={newGym.city}
                      onChange={(e) => setNewGym({ ...newGym, city: e.target.value })}
                      style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '1rem' }}
                      placeholder="Enter city"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>State *</label>
                    <input
                      type="text"
                      required
                      value={newGym.state}
                      onChange={(e) => setNewGym({ ...newGym, state: e.target.value })}
                      style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '1rem' }}
                      placeholder="Enter state"
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Country *</label>
                  <input
                    type="text"
                    required
                    value={newGym.country}
                    onChange={(e) => setNewGym({ ...newGym, country: e.target.value })}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '1rem' }}
                    placeholder="Enter country"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Subscription Plan *</label>
                  <select
                    value={newGym.subscriptionPlan}
                    onChange={(e) => setNewGym({ ...newGym, subscriptionPlan: e.target.value })}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '1rem', backgroundColor: 'white' }}
                  >
                    <option value="Trial">Trial (14 days - Free)</option>
                    <option value="Basic">Basic (₹49/month - 100 members, 5 staff)</option>
                    <option value="Pro">Pro (₹99/month - 500 members, 15 staff)</option>
                    <option value="Enterprise">Enterprise (₹199/month - Unlimited)</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Gym Logo (Optional)</label>
                  
                  {/* File Upload Option */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ 
                      display: 'inline-block', 
                      padding: '0.75rem 1.5rem', 
                      backgroundColor: '#4F46E5', 
                      color: 'white', 
                      borderRadius: '8px', 
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      transition: 'all 0.2s'
                    }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoFileChange}
                        style={{ display: 'none' }}
                      />
                      📁 Upload from Device
                    </label>
                    <span style={{ marginLeft: '1rem', color: '#6B7280', fontSize: '0.875rem' }}>or</span>
                  </div>

                  {/* URL Input Option */}
                  <input
                    type="url"
                    value={newGym.logoUrl && !newGym.logoUrl.startsWith('data:') ? newGym.logoUrl : ''}
                    onChange={(e) => {
                      setNewGym({ ...newGym, logoUrl: e.target.value });
                      setLogoPreview(e.target.value);
                      setLogoFile(null);
                    }}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '1rem' }}
                    placeholder="Enter logo image URL (e.g., https://example.com/logo.png)"
                  />
                  <small style={{ display: 'block', marginTop: '0.25rem', color: '#6B7280', fontSize: '0.875rem' }}>
                    Upload a logo from your device or enter a direct URL. This will appear on payment receipts.
                  </small>
                  
                  {/* Logo Preview */}
                  {(logoPreview || (newGym.logoUrl && newGym.logoUrl.startsWith('data:'))) && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <img 
                        src={logoPreview || newGym.logoUrl} 
                        alt="Logo Preview" 
                        style={{ maxWidth: '150px', maxHeight: '80px', border: '1px solid #D1D5DB', borderRadius: '8px', padding: '0.5rem', objectFit: 'contain' }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      {logoFile && (
                        <button
                          type="button"
                          onClick={() => {
                            setLogoPreview('');
                            setLogoFile(null);
                            setNewGym({ ...newGym, logoUrl: '' });
                          }}
                          style={{
                            marginLeft: '0.5rem',
                            padding: '0.25rem 0.75rem',
                            backgroundColor: '#EF4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.75rem'
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button
                  type="button"
                  onClick={() => setShowCreateGymModal(false)}
                  disabled={creatingGym}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    backgroundColor: '#F3F4F6',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: creatingGym ? 'not-allowed' : 'pointer',
                    opacity: creatingGym ? 0.5 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingGym}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    backgroundColor: '#3B82F6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: creatingGym ? 'not-allowed' : 'pointer',
                    opacity: creatingGym ? 0.5 : 1
                  }}
                >
                  {creatingGym ? 'Creating...' : 'Create Gym'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Credentials Modal */}
      {showCredentialsModal && newGymCredentials && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2001
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{
                width: '64px',
                height: '64px',
                backgroundColor: '#10B981',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <CheckCircle size={40} style={{ color: 'white' }} />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
                Gym Created Successfully!
              </h2>
              <p style={{ color: '#6B7280', fontSize: '0.875rem' }}>
                {newGymCredentials.gymName} has been created
              </p>
            </div>

            <div style={{
              backgroundColor: '#FEF3C7',
              border: '2px solid #F59E0B',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <AlertCircle size={20} style={{ color: '#F59E0B' }} />
                <strong style={{ color: '#92400E' }}>Login Credentials</strong>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#92400E', marginBottom: '0.5rem' }}>
                  Email:
                </label>
                <div style={{
                  backgroundColor: 'white',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  border: '1px solid #F59E0B',
                  wordBreak: 'break-all'
                }}>
                  {newGymCredentials.email}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#92400E', marginBottom: '0.5rem' }}>
                  Temporary Password:
                </label>
                <div style={{
                  backgroundColor: 'white',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  fontFamily: 'monospace',
                  fontSize: '1rem',
                  fontWeight: '700',
                  border: '1px solid #F59E0B',
                  color: '#DC2626',
                  letterSpacing: '0.05em'
                }}>
                  {newGymCredentials.password}
                </div>
              </div>
            </div>

            <div style={{
              backgroundColor: '#DBEAFE',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1.5rem'
            }}>
              <p style={{ fontSize: '0.875rem', color: '#1E40AF', margin: 0, lineHeight: '1.5' }}>
                <strong>📝 Important:</strong> {newGymCredentials.message}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => {
                  const credentials = `Gym: ${newGymCredentials.gymName}\nEmail: ${newGymCredentials.email}\nPassword: ${newGymCredentials.password}`;
                  navigator.clipboard.writeText(credentials);
                  success('Credentials copied to clipboard!');
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                📋 Copy Credentials
              </button>
              <button
                onClick={() => {
                  setShowCredentialsModal(false);
                  setNewGymCredentials(null);
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                ✓ Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Gym Modal */}
      {showDeactivateModal && selectedGym && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              Deactivate Gym: {selectedGym.gymName}
            </h3>
            <p style={{ color: '#6B7280', marginBottom: '1.5rem' }}>
              This will deactivate the gym. The gym will be moved to history section. You can reactivate it later.
            </p>
            
            <div style={{ 
              backgroundColor: '#FEF3C7', 
              border: '1px solid #F59E0B', 
              borderRadius: '8px', 
              padding: '1rem', 
              marginBottom: '1.5rem' 
            }}>
              <p style={{ color: '#92400E', fontSize: '0.875rem', margin: 0 }}>
                ⚠️ Note: This will disable access for all users of this gym. The gym data will be preserved.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowDeactivateModal(false);
                  setSelectedGym(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeactivateGym}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#F59E0B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Deactivate Gym
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Gym Modal */}
      {showDeleteModal && selectedGym && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#DC2626' }}>
              Delete Gym: {selectedGym.gymName}
            </h3>
            <p style={{ color: '#6B7280', marginBottom: '1.5rem' }}>
              This will permanently delete the gym. The gym will be moved to history section.
            </p>
            
            <div style={{ 
              backgroundColor: '#FEE2E2', 
              border: '1px solid #DC2626', 
              borderRadius: '8px', 
              padding: '1rem', 
              marginBottom: '1.5rem' 
            }}>
              <p style={{ color: '#991B1B', fontSize: '0.875rem', margin: 0, fontWeight: '600' }}>
                ⚠️ Warning: This action cannot be undone! The gym will be marked as deleted and moved to history.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedGym(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteGym}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#DC2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Delete Gym
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default EnhancedSuperAdminDashboard;

